import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'lbfs_popup_shown';
// Bump this version whenever we want existing visitors to see the popup once
// more (e.g., a fresh relaunch). Visitors who last saw version N-1 will see
// it one more time; visitors who see version N will store N and be muted.
// History: v1 = initial, v2 = pre-launch push, v3 = launch day refresh
const POPUP_VERSION = '3';

export default function WelcomePopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [step, setStep] = useState('form');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Escape hatch: visiting with ?reset=popup or ?popup=reset will clear
    // the suppression flag AND remove the query param before the check.
    // Use this to verify popup behavior from any browser without touching
    // DevTools. URL example: https://ledbyfaithstudios.com/?reset=popup
    try {
      const params = new URLSearchParams(window.location.search);
      const wants_reset = params.get('reset') === 'popup' || params.get('popup') === 'reset';
      if (wants_reset) {
        localStorage.removeItem(STORAGE_KEY);
        params.delete('reset');
        params.delete('popup');
        const clean = window.location.pathname + (params.toString() ? `?${params}` : '') + window.location.hash;
        window.history.replaceState({}, '', clean);
      }
    } catch { /* localStorage may be blocked in strict privacy modes */ }

    const lastShownVersion = localStorage.getItem(STORAGE_KEY);
    if (lastShownVersion === POPUP_VERSION) return;
    const timer = setTimeout(() => setOpen(true), 2500);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setOpen(false);
    localStorage.setItem(STORAGE_KEY, POPUP_VERSION);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${BACKEND_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: '', source: 'welcome_popup' }),
      });
    } catch { /* continue to reveal */ }
    setStep('success');
    localStorage.setItem(STORAGE_KEY, POPUP_VERSION);
    setLoading(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          data-testid="welcome-popup-overlay"
        >
          <div className="absolute inset-0 bg-[#2A2421]/60 backdrop-blur-sm" onClick={handleClose} />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md bg-[#FDFBF7] rounded-2xl shadow-2xl overflow-hidden"
            data-testid="welcome-popup-modal"
          >
            <button
              onClick={handleClose}
              data-testid="welcome-popup-close"
              className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-[#E8E1D9]/50 flex items-center justify-center text-[#7A736E] hover:text-[#2A2421] hover:bg-[#E8E1D9] transition-all duration-200"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            <div className="h-1 bg-gradient-to-r from-[#A67A5B] via-[#C59B76] to-[#A67A5B]" />

            <div className="p-8 md:p-10">
              {step === 'form' ? (
                <>
                  <div className="text-center mb-8">
                    <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#2A2421] mb-4 leading-tight">
                      Welcome to Led By Faith
                    </h2>
                    <p className="font-sans text-sm text-[#7A736E] leading-relaxed max-w-sm mx-auto">
                      Join our family and be the first to discover new arrivals, faith-filled favorites, and exclusive offers. Sign up now to unlock a limited-time discount <span className="font-semibold text-[#A67A5B]">ONLY</span> available on our <span className="font-semibold text-[#A67A5B]">NEW</span> Shopify store, <span className="font-semibold text-[#A67A5B]">CUAVENA</span>.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="welcome-popup-form">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="Enter your email address"
                      data-testid="welcome-popup-email"
                      className="w-full bg-white border border-[#E8E1D9] focus:border-[#A67A5B] px-5 py-3.5 rounded-xl outline-none transition-colors duration-300 placeholder:text-[#7A736E]/40 font-sans text-sm text-[#2A2421]"
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="welcome-popup-submit"
                      className="w-full bg-[#A67A5B] text-white py-3.5 rounded-xl font-medium tracking-wide hover:bg-[#8C654A] transition-all duration-300 disabled:opacity-50 text-sm"
                    >
                      {loading ? 'Unlocking...' : 'Unlock My Discount'}
                    </button>
                  </form>

                  <p className="text-center text-[10px] text-[#7A736E]/50 mt-4 font-sans">
                    We respect your privacy. Unsubscribe anytime.
                  </p>
                </>
              ) : (
                <div className="text-center py-2" data-testid="welcome-popup-success">
                  <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
                    <Check className="text-green-600" size={24} />
                  </div>
                  <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">
                    You're In! Here's Your Code
                  </h2>

                  <div className="bg-[#2A2421] rounded-xl p-5 mb-5">
                    <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#C59B76] mb-2">
                      Your Discount Code
                    </p>
                    <p className="font-sans text-2xl font-bold tracking-[0.15em] text-white" data-testid="welcome-popup-code">
                      FAITH18
                    </p>
                  </div>

                  <p className="font-sans text-sm text-[#7A736E] leading-relaxed mb-4 max-w-xs mx-auto">
                    Use <strong>FAITH18</strong> for <strong>18% off</strong> when purchasing through our Shopify store. This code is part of the Led By Faith website offer flow and does not apply to Amazon purchases.
                  </p>

                  <p className="font-sans text-[10px] text-[#7A736E]/50 mb-6">
                    Limited-time offer. Enter the code manually at Shopify checkout.
                  </p>

                  <a
                    href="https://cuavena.myshopify.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="welcome-popup-shop-btn"
                    className="inline-flex items-center justify-center gap-2 bg-[#A67A5B] text-white px-8 py-3 rounded-xl font-medium text-sm tracking-wide hover:bg-[#8C654A] transition-all duration-300"
                  >
                    Shop on Shopify
                  </a>
                  <button
                    onClick={handleClose}
                    className="block mx-auto mt-4 text-xs text-[#7A736E]/60 hover:text-[#7A736E] transition-colors font-sans"
                  >
                    Continue browsing
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
